import { chordSheetPath, chordSheetTooBig, compareChordSheets } from "./ChordSheet";
import type { ChordSheet } from "./ChordSheet";
import { writeDetails } from "./chordPro";
import type { Setlist } from "./Setlist";
import type { Song } from "./Song";
import type { IStorage } from "./IStorage";

/** How to save changes to a chord sheet. */
export type SaveChoice =
    /** Into the chord sheet you started from. Every setlist that plays it gets the changes. */
    | { kind: "overwrite" }
    /** As a new chord sheet for the same song, with its own name. */
    | { kind: "newVersion"; name: string }
    /** As a new chord sheet that belongs to the setlist you're editing from. The name defaults to the setlist's. */
    | { kind: "setlistOnly"; name?: string };

/** The setlist item a chord sheet is being edited from. */
export type SetlistPlace = {
    setlistId: string;
    index: number;
};

/** Everything you can do with songs, chord sheets and setlists. Works on any `IStorage`. */
export class Library {
    constructor(
        private readonly storage: IStorage,
        private readonly now = () => new Date().toISOString(),
    ) {}

    /** All songs, by title. */
    async songs(): Promise<Song[]> {
        const songs = await this.storage.getAll("songs");

        return songs.sort((a, b) => a.title.localeCompare(b.title));
    }

    async song(id: string): Promise<Song> {
        return found(await this.storage.get("songs", id), "song", id);
    }

    /** Adds a song with its first chord sheet. */
    async addSong(details: { title: string; artist?: string }, text = ""): Promise<{ song: Song; chordSheet: ChordSheet }> {
        const song: Song = { id: crypto.randomUUID(), ...details };
        const chordSheet: ChordSheet = { id: crypto.randomUUID(), songId: song.id, name: "", changedAt: this.now() };

        await this.storage.put("songs", song);
        await this.writeChordSheet(chordSheet, song, text);

        return { song, chordSheet };
    }

    /** All chord sheets, of every song. */
    async allChordSheets(): Promise<ChordSheet[]> {
        return this.storage.getAll("chordSheets");
    }

    /** The chord sheets of a song, the first one first. Setlist-only ones are left out unless asked for. */
    async chordSheets(songId: string, options: { withSetlistOnly?: boolean } = {}): Promise<ChordSheet[]> {
        const all = await this.storage.getAll("chordSheets");

        return all.filter((sheet) => sheet.songId === songId && (options.withSetlistOnly || !sheet.setlistId)).sort(compareChordSheets);
    }

    async chordSheet(id: string): Promise<ChordSheet> {
        return found(await this.storage.get("chordSheets", id), "chord sheet", id);
    }

    /** The ChordPro text of a chord sheet. */
    async text(chordSheetId: string): Promise<string> {
        return this.storage.readFile(chordSheetPath(chordSheetId));
    }

    /**
     * Saves changed ChordPro text in one of three ways, and returns the chord sheet that now holds it.
     * When editing from a setlist, a new version or setlist-only version also becomes the one that setlist plays.
     */
    async save(chordSheetId: string, text: string, choice: SaveChoice, from?: SetlistPlace): Promise<ChordSheet> {
        const original = await this.chordSheet(chordSheetId);
        const song = await this.song(original.songId);

        if (choice.kind === "overwrite") {
            const changed = { ...original, changedAt: this.now() };

            await this.writeChordSheet(changed, song, text);

            return changed;
        }

        const setlist = from && (await this.setlist(from.setlistId));

        if (choice.kind === "setlistOnly" && !setlist) throw new Error("Saving for a setlist only needs the setlist you're editing from.");

        const copy: ChordSheet = {
            id: crypto.randomUUID(),
            songId: song.id,
            name: choice.kind === "newVersion" ? choice.name : choice.name || setlist!.name,
            copiedFromId: original.id,
            changedAt: this.now(),
        };

        if (choice.kind === "setlistOnly") copy.setlistId = setlist!.id;

        await this.writeChordSheet(copy, song, text);

        if (setlist && from) await this.switchVersion(setlist.id, from.index, copy.id);

        return copy;
    }

    /** All setlists, the newest first. */
    async setlists(): Promise<Setlist[]> {
        const setlists = await this.storage.getAll("setlists");

        return setlists.sort((a, b) => (b.date ?? "").localeCompare(a.date ?? "") || a.name.localeCompare(b.name));
    }

    async setlist(id: string): Promise<Setlist> {
        return found(await this.storage.get("setlists", id), "setlist", id);
    }

    async createSetlist(name: string, date?: string): Promise<Setlist> {
        const setlist: Setlist = { id: crypto.randomUUID(), name, date, items: [] };

        await this.storage.put("setlists", setlist);

        return setlist;
    }

    async renameSetlist(id: string, name: string, date?: string): Promise<Setlist> {
        return this.changeSetlist(id, (setlist) => ({ ...setlist, name, date }));
    }

    async addToSetlist(setlistId: string, chordSheetId: string): Promise<Setlist> {
        await this.chordSheet(chordSheetId);

        return this.changeSetlist(setlistId, (setlist) => ({ ...setlist, items: [...setlist.items, { chordSheetId, transpose: 0 }] }));
    }

    async moveInSetlist(setlistId: string, from: number, to: number): Promise<Setlist> {
        return this.changeSetlist(setlistId, (setlist) => {
            const items = [...setlist.items];
            const [item] = items.splice(from, 1);

            items.splice(to, 0, item);

            return { ...setlist, items };
        });
    }

    /** Removes a song from a setlist. A setlist-only chord sheet that's no longer played is deleted with it. */
    async removeFromSetlist(setlistId: string, index: number): Promise<Setlist> {
        const before = await this.setlist(setlistId);
        const removed = before.items[index];

        const after = await this.changeSetlist(setlistId, (setlist) => ({
            ...setlist,
            items: setlist.items.filter((_, i) => i !== index),
        }));

        await this.deleteIfOrphaned(removed.chordSheetId, after);

        return after;
    }

    /** Sets how far a song in a setlist is transposed. It never changes the chord sheet. */
    async transposeInSetlist(setlistId: string, index: number, transpose: number): Promise<Setlist> {
        return this.changeSetlist(setlistId, (setlist) => ({
            ...setlist,
            items: setlist.items.map((item, i) => (i === index ? { ...item, transpose } : item)),
        }));
    }

    /** Lets a setlist play another version of the same song. */
    async switchVersion(setlistId: string, index: number, chordSheetId: string): Promise<Setlist> {
        const before = await this.setlist(setlistId);
        const previous = before.items[index].chordSheetId;

        const after = await this.changeSetlist(setlistId, (setlist) => ({
            ...setlist,
            items: setlist.items.map((item, i) => (i === index ? { ...item, chordSheetId } : item)),
        }));

        await this.deleteIfOrphaned(previous, after);

        return after;
    }

    /** Deletes a setlist and its setlist-only chord sheets. Other chord sheets stay. */
    async deleteSetlist(id: string): Promise<void> {
        const own = (await this.storage.getAll("chordSheets")).filter((sheet) => sheet.setlistId === id);

        for (const sheet of own) await this.removeChordSheet(sheet.id);

        await this.storage.delete("setlists", id);
    }

    /** The setlists that play a chord sheet. */
    async setlistsPlaying(chordSheetId: string): Promise<Setlist[]> {
        const setlists = await this.storage.getAll("setlists");

        return setlists.filter((setlist) => setlist.items.some((item) => item.chordSheetId === chordSheetId));
    }

    /** Deletes a chord sheet. Not while a setlist plays it, and not the last one of its song: delete the song instead. */
    async deleteChordSheet(id: string): Promise<void> {
        const sheet = await this.chordSheet(id);
        const playing = await this.setlistsPlaying(id);

        if (playing.length > 0) throw new Error(`Still played in ${playing.map((setlist) => setlist.name).join(", ")}.`);

        const siblings = await this.chordSheets(sheet.songId, { withSetlistOnly: true });

        if (siblings.length === 1) throw new Error("This is the song's only chord sheet. Delete the song instead.");

        await this.removeChordSheet(id);
    }

    /** Deletes a song and its chord sheets. Not while a setlist plays any of them. */
    async deleteSong(id: string): Promise<void> {
        const sheets = await this.chordSheets(id, { withSetlistOnly: true });

        for (const sheet of sheets) {
            const playing = await this.setlistsPlaying(sheet.id);

            if (playing.length > 0) throw new Error(`Still played in ${playing.map((setlist) => setlist.name).join(", ")}.`);
        }

        for (const sheet of sheets) await this.removeChordSheet(sheet.id);

        await this.storage.delete("songs", id);
    }

    private async writeChordSheet(chordSheet: ChordSheet, song: Song, text: string): Promise<void> {
        const file = writeDetails(text, song);

        if (chordSheetTooBig(file)) throw new Error("This is too long for a chord sheet.");

        await this.storage.writeFile(chordSheetPath(chordSheet.id), file);
        await this.storage.put("chordSheets", chordSheet);
    }

    private async removeChordSheet(id: string): Promise<void> {
        await this.storage.deleteFile(chordSheetPath(id));
        await this.storage.delete("chordSheets", id);
    }

    private async changeSetlist(id: string, change: (setlist: Setlist) => Setlist): Promise<Setlist> {
        const changed = change(await this.setlist(id));

        await this.storage.put("setlists", changed);

        return changed;
    }

    /** A setlist-only chord sheet goes when its setlist stops playing it. */
    private async deleteIfOrphaned(chordSheetId: string, setlist: Setlist): Promise<void> {
        const sheet = await this.storage.get("chordSheets", chordSheetId);

        if (sheet?.setlistId !== setlist.id) return;
        if (setlist.items.some((item) => item.chordSheetId === chordSheetId)) return;

        await this.removeChordSheet(chordSheetId);
    }
}

function found<T>(record: T | undefined, kind: string, id: string): T {
    if (!record) throw new Error(`There is no ${kind} with id ${id}.`);

    return record;
}
